import type { ReactNode } from 'react';
import type { NarrativeUpdate } from './narrativeUpdatePanelFragment';
import { format } from 'date-fns';
import { NarrativeUpdateFairValueBadge } from '../NarrativeUpdateFairValueBadge';
import { NarrativeUpdateShare } from '../NarrativeUpdateShare';
import { RouterLinkOptimised } from '@/components/RouterLinkOptimised';
import { useTracking } from 'react-tracking';
import { CommentNarrative } from '../CommentNarrative';
import { push } from 'redux-first-router';

type Props = {
  update: NarrativeUpdate;
  title: ReactNode;
  actions?: ReactNode;
};

export const NarrativeUpdatePanel = ({ update, title, actions }: Props) => {
  const {
    publishedAt,
    narrative,
    slug: updateSlug,
    excerpt,
    prev,
    valuation,
    commentKey,
    commentCount,
  } = update;
  const { slug, company } = narrative;
  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'update_panel',
  });
  if (!company) throw new Error('Company is missing');
  const { canonicalCommunityUrl } = company ?? {};

  const pathname = `${canonicalCommunityUrl}/${slug}/updates/${updateSlug}`;

  return (
    <Track>
      <div className="grid gap-x2 bg-surface-2 p-x2 md:p-x3 lg:rounded-x1_5">
        <div className="grid grid-rows-[theme(spacing.x4)] items-center gap-x1 md:grid-cols-[max-content_1fr]">
          <div className="grid md:col-start-2 md:row-start-1 md:justify-end">
            {actions}
          </div>
          <h2 className="m-0 grid grid-cols-[max-content_max-content] items-center gap-x1_5 font-normal md:col-start-1 md:row-start-1">
            <div className="grid grid-cols-[max-content_max-content] items-center gap-x0_5">
              <span className="text-xs text-soft md:text-base">{title}</span>
              <span className="text-xs text-solid md:text-base">
                {publishedAt && format(publishedAt, 'dd MMM yy')}
              </span>
            </div>
            {!!prev?.valuation.fairValue && !!valuation.fairValue && (
              <NarrativeUpdateFairValueBadge
                prevFairValue={prev.valuation.fairValue}
                currentFairValue={valuation.fairValue}
              />
            )}
          </h2>
        </div>
        <div>
          <p className="m-0 line-clamp-4 text-sm text-soft">{excerpt}</p>
          <RouterLinkOptimised
            to={pathname}
            className="whitespace-nowrap text-sm text-accent no-underline hover:underline"
            onClick={() => {
              trackEvent({
                action: 'click',
                modifier: 'read_more',
              });
            }}
          >
            Read more...
          </RouterLinkOptimised>
        </div>
        <div className="flex flex-wrap items-center gap-x1">
          <NarrativeUpdateShare pathname={pathname} />
          <CommentNarrative
            commentKey={commentKey}
            commentCount={commentCount}
            onCommentClick={() => push(`${pathname}#comments`)}
          />
        </div>
      </div>
    </Track>
  );
};
